import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import LoginForm from "./components/LoginForm";
import { Button } from "./components/ui/button";
import SplashScreen from "./components/SplashScreen";
import bayLogo from "./assets/bayautomation.png";
import { CameraIcon, CheckCircleIcon, PaperAirplaneIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import Fab from "./components/ui/Fab";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Tesseract from "tesseract.js";



// Wrapper where children are laid out vertically
function FabStack({ children }) {
  return (
    <div className="fixed right-6 bottom-6 z-[60] flex flex-col gap-3">
      {children}
    </div>
  );
}

export default function QCSnapTrack() {

   // Keep only VIN-legal chars (VIN excludes I,O,Q)
const VIN_ALLOWED = /[0-9]/;

// Try to pick the best VIN-like token from OCR text
function pickBestVinCandidate(text) {
  if (!text) return "";
  const tokens = text
    .toUpperCase()
    .replace(/[\s\r\n]+/g, " ")
    .split(" ")
    .map(t => t.replace(/[^A-Z0-9]/g, ""));

  // Candidates length near 17 and with only allowed chars
  const candidates = tokens.filter(t => t.length >= 11 && t.length <= 20 && [...t].every(c => VIN_ALLOWED.test(c)));

  if (!candidates.length) return "";

  // Rank: closest to 17 wins; break ties by fewer disallowed VIN letters (I/O/Q) already removed
  candidates.sort((a, b) => {
    const da = Math.abs(17 - a.length);
    const db = Math.abs(17 - b.length);
    if (da !== db) return da - db;
    return b.length - a.length; // prefer longer if same distance
  });

  // If the best is >17, try to trim common leading/trailing junk
  let best = candidates[0];
  if (best.length > 17) {
    // try sliding window to find a 17 window that still looks good
    for (let i = 0; i + 17 <= best.length; i++) {
      const win = best.slice(i, i + 17);
      if ([...win].every(c => VIN_ALLOWED.test(c))) {
        best = win;
        break;
      }
    }
  }
  return best;
}

async function ocrImageForVin(imageUrl, lang = "eng") {
  const worker = await Tesseract.createWorker();
  try {
    await worker.loadLanguage(lang);
    await worker.initialize(lang);
    // Numeric+capital bias helps; allow both since VIN has digits+letters
    await worker.setParameters({
      tessedit_char_whitelist: "0123456789",
    });
    const { data } = await worker.recognize(imageUrl);
    const vin = pickBestVinCandidate(data?.text || "");
    return vin;
  } finally {
    await worker.terminate();
  }
}

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  // Form + UI state
  const [capturedImages, setCapturedImages] = useState([]);
  const [partID, setPartID] = useState("");
  const [inspector, setInspector] = useState("");
  const [status, setStatus] = useState("Needs Review");
  const [comment, setComment] = useState("");
  const [vin, setVin] = useState("");
  const [date, setDate] = useState("");
  const [supervisorName, setSupervisorName] = useState("Nolan");
  const [supervisorID, setSupervisorID] = useState("01");
  const [editingId, setEditingId] = useState(null);
  const [resubmittingId, setResubmittingId] = useState(null); // when opened from Rejected
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vinReading, setVinReading] = useState(false);


  // Data state
  const [records, setRecords] = useState([]);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [dailyCounts, setDailyCounts] = useState([]);

  // Table interactions
  const [activeTab, setActiveTab] = useState("capture");
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const [editedComment, setEditedComment] = useState("");

  // Auth
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [currentUser, setCurrentUser] = useState("");

  // Config
  const CLOUDINARY_URL = process.env.REACT_APP_CLOUDINARY_URL;
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "bayqctracker";
  const API_BASE = "https://bayautomation-qctracker.co.za/api";

  // Date filter for approvals
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  // -------------- Helpers --------------
  const authHeaders = () =>
    token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : {};

  const fetchJSON = async (url, init = {}) => {
    const res = await fetch(url, init);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`${res.status} ${res.statusText} – ${text}`);
    }
    return res.json();
  };

  // useCallback to satisfy eslint exhaustive-deps and keep stable refs -- no date filter 
  // const loadRecords = useCallback(async () => {
  //   try {
  //     const data = await fetchJSON(`${API_BASE}/parts/sorted?sortBy=timestamp&order=desc`, {
  //       headers: { ...authHeaders() },
  //     });
  //     setRecords(data || []);
  //   } catch (e) {
  //     console.error("Failed to load records:", e);
  //   }
  // }, [API_BASE, token]);

  // Date filter added
  const loadRecords = useCallback(
    async (from = fromDate, to = toDate) => {
      try {
        const url = `${API_BASE}/parts/sorted?sortBy=timestamp&order=desc&from=${from}&to=${to}`;
        const data = await fetchJSON(url, { headers: { ...authHeaders() } });
        setRecords(data || []);
      } catch (e) {
        console.error("Failed to load records:", e);
      }
    },
    [API_BASE, token, fromDate, toDate]
  );

  const [rejectedOpen, setRejectedOpen] = useState(() => {
  // default open if not set; store "0" for closed, "1" for open
  const v = sessionStorage.getItem("rejectedOpen");
  return v !== "0";
});

  const loadRejected = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API_BASE}/rejected/all`, {
        headers: { ...authHeaders() },
      });
      setRejectedItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load rejected items:", e);
    }
  }, [API_BASE, token]);

  // -------------- Effects --------------


  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const username = payload.sub || payload.username || payload.name;
      setCurrentUser(username);
      setInspector(username);  // keep this for default form value
    } catch (err) {
      console.warn("Could not decode token:", err);
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        if (sessionStorage.getItem('force-reloaded') === '1') return;
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister().catch(() => { })));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
        sessionStorage.setItem('force-reloaded', '1');
        window.location.replace(window.location.href.split('#')[0]);
      } catch (e) {
        console.warn('Force reload failed:', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (token) localStorage.setItem("token", token);
  }, [token]);

  // Hard-unregister any old service workers (you asked for this behavior)
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) =>
        regs.forEach((r) => r.unregister().catch(() => { }))
      );
    }
  }, []);

  useEffect(() => {
  sessionStorage.setItem("rejectedOpen", rejectedOpen ? "1" : "0");
}, [rejectedOpen]);

  // Load data when opening Approvals
  useEffect(() => {
    if (!token) return;
    if (activeTab === "approvals") {
      loadRecords();
    }
  }, [activeTab, token, loadRecords, fromDate, toDate]);

  // Load rejected items when authenticated
  useEffect(() => {
    if (!token) return;
    loadRejected();
  }, [token, loadRejected]);

  // Splash
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 2500);
    return () => clearTimeout(t);
  }, []);

  // Daily totals
  useEffect(() => {
    if (!records?.length) {
      setDailyCounts([]);
      return;
    }
    const byDay = {};
    for (const rec of records) {
      const key =
        rec.date ||
        (() => {
          const d = new Date(rec.timestamp);
          return isNaN(d) ? null : d.toISOString().slice(0, 10);
        })();
      if (!key) continue;

      if (!byDay[key]) byDay[key] = { date: key, total: 0, approved: 0, pending: 0 };
      byDay[key].total += 1;
      if (rec.approved) byDay[key].approved += 1;
      else byDay[key].pending += 1;
    }
    const sorted = Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date));
    setDailyCounts(sorted);
  }, [records]);

  if (loading) return <SplashScreen />;

  // -------------- Media + Upload --------------
  const uploadToCloudinary = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    try {
      const { data } = await axios.post(CLOUDINARY_URL, formData);
      return data.secure_url;
    } catch (err) {
      console.error("Cloudinary upload failed:", err);
      alert("Upload failed. Check console for details.");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: "environment" } },
      });
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      }
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    } catch {
      // Fallback to default camera
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current?.srcObject) {
          videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        }
        videoRef.current.srcObject = fallbackStream;
        await videoRef.current.play();
      } catch (e) {
        console.error(e);
        alert("Camera access denied or unavailable");
      }
    }
  };

  const captureImage = async (indexToReplace = null) => {
    const labels = ["vin", "front", "back", "left", "right", "top-right"];
    if (!partID || !inspector || !date || !supervisorName || !supervisorID) {
      alert("Please complete all required fields before capturing an image.");
      return;
    }
    if (!videoRef.current || !canvasRef.current) return;
    if (capturedImages.length >= 6 && indexToReplace === null) {
      alert("All 6 required images are already captured.");
      return;
    }


    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async (blob) => {
        const label = labels[indexToReplace ?? capturedImages.length];
        const safeVin = (vin || "unknownVin").toUpperCase().replace(/[^A-Z0-9]/g, "");
        const file = new File([blob], `${safeVin}-${label}-${Date.now()}.jpg`, { type: "image/jpeg" });
        const imageUrl = await uploadToCloudinary(file);

        setCapturedImages((prev) => {
          const next = [...prev];
          if (indexToReplace !== null) next[indexToReplace] = imageUrl;
          else next.push(imageUrl);
          return next;
        });
              // NEW: If this was the VIN picture (5th slot), OCR it and populate VIN
      const isVinSlot =
        (indexToReplace === null && capturedImages.length === 0) || // pushing into index 4
        (indexToReplace === 0);                                      // replacing index 4
      if (isVinSlot) {
        try {
          setVinReading(true);
          const read = await ocrImageForVin(imageUrl);
          if (read && read.length >= 11) {
            setVin(read);
          } else {
            console.warn("VIN OCR: no good candidate found");
          }
        } catch (err) {
          console.error("VIN OCR failed:", err);
        } finally {
          setVinReading(false);
        }
      }
      },
      "image/jpeg",
      0.7
    );
  };

  const updateImageFromFile = async (index, file) => {
    const imageUrl = await uploadToCloudinary(file);
    setCapturedImages((prev) => {
      const next = [...prev];
      next[index] = imageUrl;
      return next;
    });
  };

  const deleteImage = (index) => {
    setCapturedImages((prev) => prev.filter((_, i) => i !== index));
  };

  // -------------- CRUD --------------
  const resetForm = () => {
    setCapturedImages([]);
    setPartID("");
    setInspector("");
    setStatus("Needs Review");
    setComment("");
    setVin("");
    setDate("");
    setSupervisorName("");
    setSupervisorID("");
    setEditingId(null);
    setResubmittingId(null);
  };

  const saveRecord = async () => {
    const nowISO = new Date().toISOString();
    // If this was opened from Rejected, force status to "Resubmitted"
    const effectiveStatus = resubmittingId ? "Resubmitted" : status;

    const recordData = {
      id: editingId ?? Date.now(),
      partID,
      inspector,
      status: effectiveStatus,
      comment,
      vin,
      date, // YYYY-MM-DD
      supervisorName,
      supervisorID,
      images: capturedImages,
      approved: false,
      timestamp: nowISO, // store a clean ISO timestamp
    };

    try {
      const method = editingId ? "PUT" : "POST";
      const url = `${API_BASE}/parts${editingId ? `/${editingId}` : ""}`;
      await fetchJSON(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(recordData),
      });

      // If resubmitting, remove from the rejected list server-side
      if (resubmittingId) {
        try {
          await fetch(`${API_BASE}/rejected/${resubmittingId}`, {
            method: "DELETE",
            headers: { ...authHeaders() },
          });
        } catch (e) {
          console.warn("Could not remove item from rejected list:", e);
        }
        await loadRejected();
      }

      if (activeTab === "approvals") {
        await loadRecords();
      } else {
        // Optimistic local update when in Capture tab
        setRecords((prev) =>
          editingId
            ? prev.map((r) => (r.id === editingId ? recordData : r))
            : [recordData, ...prev]
        );
      }
      resetForm();
      alert(resubmittingId ? "Record resubmitted for review." : "Record saved.");
    } catch (e) {
      console.error("Save failed:", e);
      alert("Save failed. Check console for details.");
    }
  };

  const updateComment = async (id, newComment) => {
    try {
      await fetchJSON(`${API_BASE}/parts/${id}/comment`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ comment: newComment }),
      });
      await loadRecords();
    } catch (e) {
      console.error("Failed to update comment:", e);
      alert("Failed to update comment.");
    }
  };

  const approveRecord = async (id) => {
    try {
      await fetchJSON(`${API_BASE}/parts/${id}/approve`, {
        method: "PUT",
        headers: { ...authHeaders() },
      });
      setRecords((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, approved: true, status: "Approved" } : r
        )
      );
    } catch (e) {
      console.error("Approve failed:", e);
      alert("Approve failed.");
    }
  };

  const rejectRecord = async (id) => {
    const reason = prompt("Enter reason for rejection:");
    if (!reason) return;

    const rejectedItem = records.find((r) => r.id === id);
    if (!rejectedItem) return;

    try {
      // 1) Mark the part as rejected (server-side)
      await fetchJSON(`${API_BASE}/parts/${id}/reject`, {
        method: "PUT",
        headers: { ...authHeaders() },
      });

      // 2) Create a rejected-item entry (server-side)
      const payload = {
        ...rejectedItem,
        reason,
        rejectedBy: supervisorName || "Supervisor",
        timestamp: new Date().toISOString(),
      };
      await fetchJSON(`${API_BASE}/rejected`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      await Promise.all([loadRecords(), loadRejected()]);
    } catch (e) {
      console.error("Reject flow failed:", e);
      alert("Reject failed.");
    }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setPartID(record.partID || "");
    setInspector(record.inspector || "");
    setStatus(record.status || "Needs Review");
    setComment(record.comment || "");
    setVin(record.vin || "");
    setDate(record.date || "");
    setSupervisorName(record.supervisorName || "");
    setSupervisorID(record.supervisorID || "");
    setCapturedImages(record.images || []);
    setActiveTab("capture");
  };

  // Auth gate
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoginForm setToken={setToken} />
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken("");
  };



  // -------------- Render --------------
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img
            src={bayLogo} 
            alt="Bay Automation"
            className="h-8 w-auto"
          />
          <span className="text-2xl font-bold">QC SnapTrack</span>
        </div>

        <Button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 text-white">
          Logout
        </Button>
      </div>

      {activeTab === "capture" && rejectedItems.length > 0 && (
  <div className="border rounded bg-yellow-50 text-yellow-900 shadow">
    {/* Header */}
    <button
      type="button"
      onClick={() => setRejectedOpen(o => !o)}
      className="w-full flex items-center justify-between gap-3 px-4 py-3"
      aria-expanded={rejectedOpen}
      aria-controls="rejected-items-panel"
    >
      <div className="flex items-center gap-3">
        <h2 className="font-semibold">Rejected Items</h2>
        <span className="inline-flex items-center justify-center text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-600 text-white">
          {rejectedItems.length}
        </span>
      </div>
      <ChevronDownIcon
        className={`h-5 w-5 transition-transform duration-200 ${rejectedOpen ? "rotate-180" : ""}`}
      />
    </button>

    {/* Collapsible Body */}
    {rejectedOpen && (
      <div id="rejected-items-panel" className="px-4 pb-4">
        <ul className="list-disc list-inside max-h-64 overflow-auto pr-2">
          {rejectedItems.map((item) => (
            <li
              key={item.id}
              className="cursor-pointer hover:text-blue-700 py-1"
              onClick={() => {
                setResubmittingId(item.id); // mark as resubmission
                editRecord(item);
                setActiveTab("capture");
              }}
            >
              <div className="font-medium">
                {item.partID} • {item.inspector} • {item.vin}
              </div>
              <div className="text-sm italic">
                Reason: {item.reason} | Time: {new Date(item.timestamp).toISOString()} | By: {item.rejectedBy}
              </div>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
)}


      <div className="sticky top-0 bg-white z-10">
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            className={`w-full flex items-center justify-center gap-2 py-3 text-center font-medium rounded-t border-b-4 transition ${activeTab === "capture"
              ? "border-blue-600 text-blue-600 bg-gray-100"
              : "border-transparent text-gray-500 bg-white"
              }`}
            onClick={() => setActiveTab("capture")}
          >
            <CameraIcon className="h-5 w-5" /> Capture
          </button>
          <button
            className={`w-full flex items-center justify-center gap-2 py-3 text-center font-medium rounded-t border-b-4 transition ${activeTab === "approvals"
              ? "border-blue-600 text-blue-600 bg-gray-100"
              : "border-transparent text-gray-500 bg-white"
              }`}
            onClick={() => setActiveTab("approvals")}
          >
            <CheckCircleIcon className="h-5 w-5" /> Approvals
          </button>
        </div>
      </div>

      {activeTab === "approvals" && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Approval Records</h2>

          {/* Daily totals */}
          <div className="space-y-2">
            <h3 className="text-md font-semibold">Daily Totals</h3>
            <div className="overflow-auto">
              {/* Filter Controls */}
              <div className="flex items-center gap-4 mb-4">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border rounded p-2"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border rounded p-2"
                />

                <Button
                  onClick={async () => {
                    // reload with selected range
                    await loadRecords(fromDate, toDate);
                  }}
                >
                  Apply
                </Button>

                <Button
                  onClick={async () => {
                    // last 7 days shortcut
                    const end = new Date();
                    const start = new Date();
                    start.setDate(end.getDate() - 6);

                    const f = start.toISOString().slice(0, 10);
                    const t = end.toISOString().slice(0, 10);

                    setFromDate(f);
                    setToDate(t);
                    await loadRecords(f, t);
                  }}
                >
                  Last 7 Days
                </Button>
              </div>
              <table className="min-w-full table-auto border text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 border text-left">Date</th>
                    <th className="px-4 py-2 border text-right">Total</th>
                    <th className="px-4 py-2 border text-right">Approved</th>
                    <th className="px-4 py-2 border text-right">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyCounts.map((d) => (
                    <tr key={d.date}>
                      <td className="px-4 py-2 border">{d.date}</td>
                      <td className="px-4 py-2 border text-right">{d.total}</td>
                      <td className="px-4 py-2 border text-right">{d.approved}</td>
                      <td className="px-4 py-2 border text-right">{d.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Records */}
          <div className="overflow-auto">
            <table className="min-w-full table-auto border text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 border">Part ID</th>
                  <th className="px-4 py-2 border">Inspector</th>
                  <th className="px-4 py-2 border">VIN</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Timestamp</th>
                  <th className="px-4 py-2 border">Approved</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <React.Fragment key={rec.id}>
                    <tr
                      onClick={() => {
                        setExpandedRecordId(expandedRecordId === rec.id ? null : rec.id);
                        setEditedComment(rec.comment || "");
                      }}
                      className="cursor-pointer hover:bg-blue-50"
                    >
                      <td className="px-4 py-2 border">{rec.partID}</td>
                      <td className="px-4 py-2 border">{rec.inspector}</td>
                      <td className="px-4 py-2 border">{rec.vin}</td>
                      <td className="px-4 py-2 border">{rec.status}</td>
                      <td className="px-4 py-2 border">{rec.timestamp}</td>
                      <td className="px-4 py-2 border">{rec.approved ? "✅" : "❌"}</td>
                    </tr>
                    {expandedRecordId === rec.id && (
                      <tr>
                        <td colSpan="6" className="p-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            {rec.images?.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`snap-${idx}`}
                                className="w-full h-40 object-cover rounded shadow"
                              />
                            ))}
                          </div>
                          <div className="mb-4">
                            <label className="font-medium block mb-1">Comment:</label>
                            <textarea
                              value={editedComment}
                              onChange={(e) => setEditedComment(e.target.value)}
                              rows={3}
                              className="border rounded w-full p-2"
                            />
                            <Button className="mt-2" onClick={() => updateComment(rec.id, editedComment)}>
                              Save Comment
                            </Button>
                          </div>
                          <div className="flex gap-4">
                            <Button onClick={() => approveRecord(rec.id)}>Approve</Button>
                            <Button variant="destructive" onClick={() => rejectRecord(rec.id)}>
                              Reject
                            </Button>
                            {/* 🔻 Only show the delete button when the authenticated user is Thuven */}
                            {currentUser === "Thuven" && (
                              <Button
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm("Are you sure you want to delete this record?")) {
                                    fetch(`${API_BASE}/parts/${rec.id}`, {
                                      method: "DELETE",
                                      headers: { ...authHeaders() },
                                    }).then(() => loadRecords());
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "capture" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            {capturedImages.length < 6 && (
              <div className="mb-2 p-2 text-blue-800 font-medium text-center animate-pulse bg-blue-100 border border-blue-300 rounded">
                Please capture: {["VIN", "Front", "Back", "Left", "Right", "Top-Right"][capturedImages.length]}
              </div>
            )}
            <video ref={videoRef} className="rounded w-full" />
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-2 mt-2">
              <Button onClick={startCamera}>Start Camera</Button>
              <Button onClick={() => captureImage()}>Capture Image ({capturedImages.length}/6)</Button>
            </div>
          </div>

          <div className="space-y-4 p-6 rounded-2xl bg-white/30 backdrop-blur-md border border-gray-300 shadow-xl">
            <select
            className="border border-gray-300 rounded-lg w-full p-3 bg-white/70 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            required
            value={partID}
            onChange={(e) => setPartID(e.target.value)}
            >
              <option value="" disabled>Select Part</option>
              <option value="Old part">Old part</option>
              <option value="New part">New part</option>
              <option value="DC-DC">New part</option>
            </select>
            <input
              type="text"
              placeholder="Inspector Name"
              className="border rounded w-full p-2"
              value={inspector}
              onChange={(e) => setInspector(e.target.value)}
            />
            <input
    type="text"
    placeholder="VIN"
    className="border rounded w-full p-2"
    value={vin}
    onChange={(e) => setVin(e.target.value)}
  />
  <Button
    type="button"
    disabled={!capturedImages[0] || vinReading}
    onClick={async () => {
      if (!capturedImages[0]) {
        alert("Please capture or upload the VIN image (5th slot) first.");
        return;
      }
      try {
        setVinReading(true);
        const read = await ocrImageForVin(capturedImages[0]);
        if (read && read.length >= 11) {
          setVin(read);
        } else {
          alert("Could not confidently read a VIN. Try a clearer VIN photo (sharp focus, good lighting).");
        }
      } catch (e) {
        console.error(e);
        alert("VIN reading failed. See console for details.");
      } finally {
        setVinReading(false);
      }
    }}
    className="whitespace-nowrap"
    title="Read VIN from the VIN image"
  >
    {vinReading ? "Reading…" : "Read VIN"}
  </Button>
            <input
              type="date"
              className="border rounded w-full p-2"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <input
              type="text"
              placeholder="Supervisor Name"
              className="border rounded w-full p-2"
              value={supervisorName}
              onChange={(e) => setSupervisorName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Supervisor ID"
              className="border rounded w-full p-2"
              value={supervisorID}
              onChange={(e) => setSupervisorID(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-lg w-full p-3 bg-white/70 text-gray-500 cursor-not-allowed"
              value={resubmittingId ? "Resubmitted" : status}
              disabled
            >
              <option value="Needs Review">Needs Review</option>
              <option value="Resubmitted">Resubmitted</option>
            </select>
            <textarea
              placeholder="Comments"
              className="border rounded w-full p-2"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button onClick={saveRecord} disabled={capturedImages.length !== 6 || uploading}>
              {editingId ? (resubmittingId ? "Resubmit Record" : "Update Record") : "Save Record"}
            </Button>

            {capturedImages.length > 0 && (
              <div className="mt-4 p-2 border rounded bg-gray-50">
                <h3 className="font-semibold mb-2">Captured Images</h3>
                <div className="grid grid-cols-3 gap-4">
                  {capturedImages.map((img, idx) => {
                    const labels = ["VIN", "Front", "Back", "Left", "Right", "Top-Right"];
                    return (
                      <div
                        key={idx}
                        className="relative w-28 h-28 border border-gray-300 rounded-xl overflow-hidden shadow-md bg-white/50 backdrop-blur-sm transform transition duration-300 hover:scale-105 hover:shadow-xl hover:ring-2 hover:ring-blue-400"
                      >
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs text-center py-1">
                          {labels[idx] || `Image ${idx + 1}`}
                        </div>
                        <img src={img} alt={`Snap ${idx}`} className="w-full h-full object-cover" />
                        <div className="absolute top-0 right-0 flex flex-col gap-1 m-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id={`file-input-${idx}`}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) updateImageFromFile(idx, file);
                            }}
                          />
                          <label htmlFor={`file-input-${idx}`}>
                            <Button className="text-xs px-1 py-0.5 w-full">Update</Button>
                          </label>
                          <Button className="text-xs px-1 py-0.5 w-full" onClick={() => deleteImage(idx)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <FabStack>
  <Fab
    onClick={() => navigate("/comms")}
    icon={PaperAirplaneIcon}
    label="Open Comms"
    className=""
  />
  <Fab
    onClick={() => navigate("/stock")}
    icon={ClipboardDocumentListIcon}
    label="Stock Take"
    className=""
  />
</FabStack>
    </div>
  );
}
